import { useCallback, useRef } from 'react';

interface AbsolutePos {
  x: number;
  y: number;
}

export interface UseFrameDragCacheReturn {
  /**
   * Snapshot the absolute positions of the listed parentIds as of drag-start.
   * Frames the resolver returns null for are skipped (unknown / not yet
   * rendered) — the cache only carries genuine snapshots.
   */
  recordDragStart: (
    parentIds: readonly string[],
    resolveAbsolute: (parentId: string) => AbsolutePos | null,
  ) => void;
  /** Returns the cached absolute position for `parentId`, or null if uncached. */
  getCachedAbsolute: (parentId: string) => AbsolutePos | null;
  /** Wipe the cache (e.g. on drag-stop). */
  clearCache: () => void;
}

/**
 * Cache for frame absolute positions across a drag's lifetime.
 *
 * Per client.md §6, when a remote peer deletes a frame mid-drag, the dragged
 * child's renderer must continue painting at the cached parent position
 * rather than reading a now-null parentId chain (which would jump the child
 * to the canvas origin). This hook is the storage layer; CanvasEngine wires
 * recordDragStart to onNodeDragStart, getCachedAbsolute into the spatial
 * projection's parent-lookup fallback, and clearCache to onNodeDragStop.
 *
 * The cache is intentionally a `useRef` Map (not state) — it's read on every
 * spatial recompute but never drives a rerender on its own. Mutations are
 * paired with the React Flow drag callbacks which already trigger renders.
 */
export function useFrameDragCache(): UseFrameDragCacheReturn {
  const cacheRef = useRef<Map<string, AbsolutePos>>(new Map());

  const recordDragStart = useCallback(
    (
      parentIds: readonly string[],
      resolveAbsolute: (parentId: string) => AbsolutePos | null,
    ) => {
      for (const id of parentIds) {
        const pos = resolveAbsolute(id);
        if (pos) cacheRef.current.set(id, pos);
      }
    },
    [],
  );

  const getCachedAbsolute = useCallback(
    (parentId: string): AbsolutePos | null => cacheRef.current.get(parentId) ?? null,
    [],
  );

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  return { recordDragStart, getCachedAbsolute, clearCache };
}
